import React, { useState } from "react";

const DMCAOpen = () => {
  const [formData, setFormData] = useState({
    claimantName: "",
    signature: "",
    copyrightedWork: "",
    infringingMaterial: "",
    contactEmail: "",
    contactPhone: "",
    goodFaithConfirmed: false,
    perjuryConfirmed: false,
  });

  const [hasPreparedEmail, setHasPreparedEmail] = useState(false);
  const [copyStatus, setCopyStatus] = useState("");
  const [emailDraft, setEmailDraft] = useState({ subject: "", body: "" });

  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, "").slice(0, 10);
    if (digits.length === 0) return "";
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)})-${digits.slice(3)}`;
    return `(${digits.slice(0, 3)})-${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : name === "contactPhone"
          ? formatPhoneNumber(value)
          : value,
    }));
  };

  // Static recipient (masked to avoid plain-text literal in source)
  const dmcaRecipient = ["info", "readwellllc.com"].join("@");

  const handleSubmit = (event) => {
    event.preventDefault();

    const recipient = dmcaRecipient;
    const subject = `DMCA Takedown Notice - ${formData.copyrightedWork || "Unspecified Work"}`;
    const body = [
      "DMCA TAKEDOWN NOTICE",
      "",
      `Claimant Name: ${formData.claimantName}`,
      `Physical/Electronic Signature: ${formData.signature}`,
      `Copyrighted Work Claimed: ${formData.copyrightedWork}`,
      `Material to Remove (URL on ReadRivals): ${formData.infringingMaterial}`,
      `Contact Email: ${formData.contactEmail}`,
      `Contact Phone: ${formData.contactPhone}`,
      `Good Faith Statement Confirmed: ${formData.goodFaithConfirmed ? "Yes" : "No"}`,
      `Accuracy/Perjury Statement Confirmed: ${formData.perjuryConfirmed ? "Yes" : "No"}`,
      "",
      `Submitted At (UTC): ${new Date().toISOString()}`,
    ].join("\n");

    setEmailDraft({ subject, body });
    setHasPreparedEmail(true);
    setCopyStatus("");
  };

  const openDefaultMailApp = () => {
    window.location.href = `mailto:${dmcaRecipient}?subject=${encodeURIComponent(emailDraft.subject)}&body=${encodeURIComponent(emailDraft.body)}`;
  };

  const openGmailCompose = () => {
    const url = `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(dmcaRecipient)}&su=${encodeURIComponent(emailDraft.subject)}&body=${encodeURIComponent(emailDraft.body)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openOutlookCompose = () => {
    const url = `https://outlook.office.com/mail/deeplink/compose?to=${encodeURIComponent(dmcaRecipient)}&subject=${encodeURIComponent(emailDraft.subject)}&body=${encodeURIComponent(emailDraft.body)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const copyNotice = async () => {
    const payload = `To: ${dmcaRecipient}\nSubject: ${emailDraft.subject}\n\n${emailDraft.body}`;
    try {
      await navigator.clipboard.writeText(payload);
      setCopyStatus("Notice copied. Paste it into any email service and send.");
    } catch (_error) {
      setCopyStatus("Copy failed. Please select and copy the notice text manually.");
    }
  };

  return (
    <div style={{ maxWidth: "820px", margin: "0 auto", padding: "20px" }}>
      <h1>Report Copyright Infringement (DMCA)</h1>
      <p>
        Use this page to submit a DMCA takedown notice to ReadRivals LLC. Complete all required
        fields below and submit your notice.
      </p>

      <h2 style={{ marginTop: "1.5rem" }}>Required Notice Elements</h2>
      <ol style={{ paddingLeft: "1.2rem" }}>
        <li>A physical or electronic signature of the copyright owner.</li>
        <li>Identification of the copyrighted work claimed to have been infringed.</li>
        <li>Identification of the material on this site that should be removed (URL).</li>
        <li>Claimant contact information (email and phone number).</li>
        <li>A statement of good faith belief that the use is unauthorized.</li>
        <li>A statement of accuracy under penalty of perjury.</li>
      </ol>

      <form onSubmit={handleSubmit} style={{ marginTop: "1.5rem", display: "grid", gap: "0.9rem" }}>
        <label>
          Claimant Full Name
          <input
            type="text"
            name="claimantName"
            value={formData.claimantName}
            onChange={handleChange}
            required
            style={{ width: "100%", marginTop: "0.35rem", padding: "0.55rem" }}
          />
        </label>

        <label>
          Physical or Electronic Signature
          <input
            type="text"
            name="signature"
            value={formData.signature}
            onChange={handleChange}
            placeholder="Type full legal name to sign electronically"
            required
            style={{ width: "100%", marginTop: "0.35rem", padding: "0.55rem" }}
          />
        </label>

        <label>
          Identify Copyrighted Work (e.g., link to original book)
          <textarea
            name="copyrightedWork"
            value={formData.copyrightedWork}
            onChange={handleChange}
            rows={3}
            required
            style={{ width: "100%", marginTop: "0.35rem", padding: "0.55rem", resize: "vertical" }}
          />
        </label>

        <label>
          Identify Infringing Material on ReadRivals (URL to remove)
          <textarea
            name="infringingMaterial"
            value={formData.infringingMaterial}
            onChange={handleChange}
            rows={3}
            required
            style={{ width: "100%", marginTop: "0.35rem", padding: "0.55rem", resize: "vertical" }}
          />
        </label>

        <div style={{ display: "grid", gap: "0.9rem", gridTemplateColumns: "1fr 1fr" }}>
          <label>
            Contact Email
            <input
              type="email"
              name="contactEmail"
              value={formData.contactEmail}
              onChange={handleChange}
              required
              style={{ width: "100%", marginTop: "0.35rem", padding: "0.55rem" }}
            />
          </label>

          <label>
            Contact Phone Number
            <input
              type="tel"
              name="contactPhone"
              value={formData.contactPhone}
              onChange={handleChange}
              placeholder="(383)-323-3322"
              inputMode="numeric"
              maxLength={14}
              required
              style={{ width: "100%", marginTop: "0.35rem", padding: "0.55rem" }}
            />
          </label>
        </div>

        <label style={{ display: "flex", alignItems: "flex-start", gap: "0.55rem" }}>
          <input
            type="checkbox"
            name="goodFaithConfirmed"
            checked={formData.goodFaithConfirmed}
            onChange={handleChange}
            required
            style={{ marginTop: "0.2rem" }}
          />
          <span>
            I state that I have a good faith belief that the disputed use is not authorized by the
            copyright owner, its agent, or the law.
          </span>
        </label>

        <label style={{ display: "flex", alignItems: "flex-start", gap: "0.55rem" }}>
          <input
            type="checkbox"
            name="perjuryConfirmed"
            checked={formData.perjuryConfirmed}
            onChange={handleChange}
            required
            style={{ marginTop: "0.2rem" }}
          />
          <span>
            I state under penalty of perjury that the information in this notice is accurate and
            that I am the copyright owner or authorized to act on behalf of the owner.
          </span>
        </label>

        <button
          type="submit"
          style={{
            justifySelf: "start",
            border: "none",
            borderRadius: "6px",
            padding: "0.65rem 1.2rem",
            background: "#1d4ed8",
            color: "#fff",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          Submit DMCA Notice
        </button>
      </form>

      {hasPreparedEmail && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Send DMCA Notice"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
            zIndex: 1300,
          }}
        >
          <div
            style={{
              width: "min(760px, 100%)",
              background: "#fff",
              borderRadius: "12px",
              padding: "1.25rem",
              boxShadow: "0 18px 45px rgba(15, 23, 42, 0.28)",
            }}
          >
            <p style={{ marginTop: 0, color: "#14532d", fontWeight: 700, fontSize: "1.05rem" }}>
              Your DMCA notice draft is ready.
            </p>
            <p style={{ marginTop: 0 }}>Choose how you want to send it:</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.6rem", marginBottom: "0.75rem" }}>
              <button type="button" onClick={openGmailCompose} style={{ border: "1px solid #cbd5e1", borderRadius: "6px", padding: "0.5rem 0.8rem", background: "#fff", cursor: "pointer" }}>
                Send with Gmail Web
              </button>
              <button type="button" onClick={openOutlookCompose} style={{ border: "1px solid #cbd5e1", borderRadius: "6px", padding: "0.5rem 0.8rem", background: "#fff", cursor: "pointer" }}>
                Send with Outlook Web
              </button>
              <button type="button" onClick={openDefaultMailApp} style={{ border: "1px solid #cbd5e1", borderRadius: "6px", padding: "0.5rem 0.8rem", background: "#fff", cursor: "pointer" }}>
                Open Default Mail App
              </button>
              <button type="button" onClick={copyNotice} style={{ border: "1px solid #cbd5e1", borderRadius: "6px", padding: "0.5rem 0.8rem", background: "#fff", cursor: "pointer" }}>
                Copy Full Notice
              </button>
            </div>
            {copyStatus && <p style={{ marginTop: 0, color: "#334155" }}>{copyStatus}</p>}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "0.5rem" }}>
              <button
                type="button"
                onClick={() => setHasPreparedEmail(false)}
                style={{
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  padding: "0.5rem 0.9rem",
                  background: "#fff",
                  cursor: "pointer",
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DMCAOpen;
